import type { Dish, Maybe, TransactionInventory } from "types";

export interface EffectiveRefillLedger {
	effectiveRemoved: Dish[];
	effectiveAdded: Dish[];
}

/**
 * Computes what one refill frame's inventory means for the Meal List, given the lists applied on
 * the previous frame (JID-2303). The BE lists are NET-cumulative vs the refill start and re-netted
 * non-monotonically (measured on live captures, 2026-07-29):
 *  - a dish added and then taken back DROPS OUT of `added` (it never permanently enters
 *    `removed`, which tracks original stock) — fold that dropout into the removals, or the dish
 *    stays as a phantom that keeps its emptied type on the Meal List;
 *  - the SAME dish can transiently appear in `removed` (STOCK_CHECK) and drop out of it again
 *    (FINISHED) — a `removed`-dropout is a restored ORIGINAL dish only if this pass never listed
 *    it in `added`, so those re-adds are filtered through `everAddedDishIds`.
 * Records every id in `inventory.added` into `everAddedDishIds` as part of its contract — the
 * caller owns the set and clears it when a new refill pass starts. The effective lists never
 * overlap (the per-frame lists are disjoint, and each revocation comes from the opposite prior
 * list), so applying removed-then-added stays deterministic.
 */
export const computeEffectiveRefillLedger = (
	prevInventory: Maybe<TransactionInventory> | undefined,
	inventory: Maybe<TransactionInventory> | undefined,
	everAddedDishIds: Set<string>,
): EffectiveRefillLedger => {
	const removed = inventory?.removed ?? [];
	const added = inventory?.added ?? [];
	const removedIds = new Set(removed.map((dish) => dish.id));
	const addedIds = new Set(added.map((dish) => dish.id));
	for (const dish of added) {
		everAddedDishIds.add(dish.id);
	}
	const revokedAdds = (prevInventory?.added ?? []).filter((dish) => !addedIds.has(dish.id));
	const revokedRemovals = (prevInventory?.removed ?? []).filter(
		(dish) => !(removedIds.has(dish.id) || everAddedDishIds.has(dish.id)),
	);
	return {
		effectiveRemoved: [...removed, ...revokedAdds],
		effectiveAdded: [...added, ...revokedRemovals],
	};
};
